import { Box, Typography, Stack, Chip, Skeleton } from '@mui/material';
import { Storefront } from '@mui/icons-material';
import { useQuery } from '@tanstack/react-query';
import { dealersApi } from '../api/dealers.api';

/**
 * Compact dealer identity panel (name/code + GST + PAN + primary address) shown across the order
 * lifecycle — Place Order, Order Confirmation, Dispatch and Delivery — so there is no confusion
 * about which dealer an order belongs to. Self-fetching + cached; scoped server-side (a dealer
 * portal user only resolves their own dealer).
 */
export function DealerSummaryCard({ dealerId, dense = false }: { dealerId: number | null | undefined; dense?: boolean }) {
  const enabled = Boolean(dealerId);
  const dealer = useQuery({ queryKey: ['dealer-summary', dealerId], queryFn: () => dealersApi.get(dealerId as number), enabled });
  const addresses = useQuery({ queryKey: ['dealer-summary-addr', dealerId], queryFn: () => dealersApi.listAddresses(dealerId as number), enabled });

  if (!dealerId) return null;
  const d = dealer.data;
  const list = addresses.data ?? [];
  const primary = list.find((a) => a.isPrimary) ?? list[0];
  const addr = primary ? [primary.line1, primary.line2, primary.city, primary.pincode].filter(Boolean).join(', ') : '—';

  return (
    <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, p: dense ? 1 : 1.5, bgcolor: 'action.hover' }}>
      <Stack direction="row" spacing={1} alignItems="center" mb={0.5}>
        <Storefront fontSize="small" color="primary" />
        <Typography variant="subtitle2">Dealer details</Typography>
      </Stack>
      {dealer.isLoading ? (
        <Skeleton variant="rounded" height={44} />
      ) : d ? (
        <Stack spacing={0.5}>
          <Typography variant="body2"><b>{d.dealerName}</b>{d.dealerCode ? ` (${d.dealerCode})` : ''}</Typography>
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            <Chip size="small" variant="outlined" label={`GST: ${d.gstNumber ?? '—'}`} />
            <Chip size="small" variant="outlined" label={`PAN: ${d.panNumber ?? '—'}`} />
          </Stack>
          <Typography variant="caption" color="text.secondary">Address: {addr}</Typography>
        </Stack>
      ) : (
        <Typography variant="caption" color="text.secondary">Dealer details unavailable.</Typography>
      )}
    </Box>
  );
}
